import React, { Fragment, useEffect, useState } from "react";
import { SimpleList, AutocompleteInput, BooleanInput, Datagrid, DateField, List, Pagination, ReferenceField, ReferenceInput, TextField, TextInput, useNotify, useRefresh, useUpdate } from "react-admin";
import JsonModalField from "../../components/JsonModalField";
import { totalAmount as totalAmountService } from "../../services/myPosDirectTransaction.service";
import { translate } from "../../i18n/customI18nProvider";
import { getRight } from "../../services/storage";
import config from '../../config';
import { JobStatus } from "../../components/reactAdmin/JobStatus";
import { ArchiveButton, BulkArchiveButton} from "./ArchiveButton";
import QuickFilter from "../../components/common/quickFilter";
import EventFilter from "./EventFilter";
import withWidth from '@material-ui/core/withWidth';
import { useMediaQuery } from "@material-ui/core";
import { borderBottom } from "@mui/system";
import ResponsiveDatagrid from "../../components/reactAdmin/ResponsiveDatagrid";
import { Archive, Delete } from "@mui/icons-material";
import { notifySuccess } from "../../util";
import JsonModal from "../../components/JsonModal";

function StatTile({ title, value, color }) {
    let displayColor = null;
    switch (color) {
        case 'red':
            displayColor = '#d32f2f';
            break;
        case 'green':
            displayColor = '#388e3c';
            break;
        case 'blue':
            displayColor = '#1976d2';
            break;
        default:
            displayColor = '#222';
    }
    return (
        <div style={{
            background: '#ffffff',
            borderRadius: 8,
            padding: '12px 16px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
        }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{title}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: color }}>{value}</div>
        </div>
    );
}

function Summary({ refreshSummary }) {
    const [totalAmount, setTotalAmount] = useState({ totalAmountEvent: { debit: 0, credit: 0 }, totalAmountAll: null });
    const [loading, setLoading] = useState(true);
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchTotalAmount();
    }, [refreshSummary]);

    async function fetchTotalAmount() {
        try {
            const response = await totalAmountService();
            setTotalAmount(response);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    const ev = totalAmount.totalAmountEvent || { debit: 0, credit: 0 };
    const evDebit = (ev.debit || 0).toFixed(2);
    const evCredit = (ev.credit || 0).toFixed(2);
    const evNet = ((ev.debit || 0) - (ev.credit || 0)).toFixed(2);

    const all = totalAmount.totalAmountAll;
    const allDebit = all ? (all.debit || 0).toFixed(2) : null;
    const allCredit = all ? (all.credit || 0).toFixed(2) : null;
    const allNet = all ? ((all.debit || 0) - (all.credit || 0)).toFixed(2) : null;

    const debitTxt = translate('custom.debit');
    const creditTxt = translate('custom.credit');
    const netTxt = translate('custom.solde');

    const firstDebitTxt = all ? translate('custom.mypos_direct.total_amount_0_event') : translate('custom.mypos_direct.total_amount_all');
    const firstCreditTxt = all ? translate('custom.mypos_direct.total_amount_0_event') : translate('custom.mypos_direct.total_amount_all');
    const firstNetTxt = all ? translate('custom.mypos_direct.total_amount_0_event') : translate('custom.mypos_direct.total_amount_all');

    if(isSmall) {
        return (
            <StatTile small={isSmall} title={`${firstNetTxt} (${netTxt})`} value={loading ? '…' : `CHF ${evNet}`} />
        )
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <StatTile small={isSmall} color="green" title={`${firstDebitTxt} (${debitTxt})`} value={loading ? '…' : `CHF ${evDebit}`} />
                <StatTile small={isSmall} color="red" title={`${firstCreditTxt} (${creditTxt})`} value={loading ? '…' : `CHF ${evCredit}`} />
                <StatTile small={isSmall} title={`${firstNetTxt} (${netTxt})`} value={loading ? '…' : `CHF ${evNet}`} />
            </div>

            {all ? (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <StatTile small={isSmall} color="green" title={`${translate('custom.mypos_direct.total_amount_all')} (${debitTxt})`} value={loading ? '…' : `CHF ${allDebit}`} />
                    <StatTile small={isSmall} color="red" title={`${translate('custom.mypos_direct.total_amount_all')} (${creditTxt})`} value={loading ? '…' : `CHF ${allCredit}`} />
                    <StatTile small={isSmall} title={`${translate('custom.mypos_direct.total_amount_all')} (${netTxt})`} value={loading ? '…' : `CHF ${allNet}`} />
                </div>
            ) : null}
        </div>
    );
}

function AmountField({ record = {}, source }) {
    const value = record && (record[source] || record.amount || 0);
    const type = (record && record.type) ? String(record.type).toLowerCase() : '';

    // detect debit / credit in several possible language forms
    const isDebit = /debit|débit/.test(type);
    const isCredit = /credit|crédit/.test(type);

    const num = Number(value) || 0;
    const abs = Math.abs(num).toFixed(2);
    const sign = isDebit ? '+' : isCredit ? '-' : '';
    const color = isDebit ? '#388e3c' : isCredit ? '#d32f2f' : '#222';

    // Render sign in a fixed-width element so + and - align, use tabular numbers for stable digits
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', color, fontWeight: 600 }}>
            {/* fixed-width sign column */}
            <span style={{ width: 14, textAlign: 'left', display: 'inline-block', marginRight: 0 }}>{sign || '\u00A0'}</span>
            {/* tabular numbers keep digits monospaced for alignment */}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{abs}</span>
            <span style={{ marginLeft: 4, fontSize: 14 }}>CHF</span>
        </span>
    );
}

const filters = getRight() < config.permissions.SUPERADMIN.value ? [] : [
    <EventFilter key="event-filter" alwaysOn />,
    <QuickFilter key="quick-filter-archived" source="archived" label={'custom.non_archived'} defaultValue={false} />
];

const PostBulkActionButtons = props => {
    return (
        <Fragment>
            <BulkArchiveButton {...props} refreshSummary={props.refreshSummary} />
        </Fragment>
    );
}

export const MyPosDirectList = (props) => {    
    const [update, { loading }] = useUpdate();
    const refresh = useRefresh();
    const [jsonModal, setJsonModal] = useState({ open: false, data: null });
    const [refreshSummary, setRefreshSummary] = useState(0);

    const showJsonDetails = (e) => {
        const data = e.myposData || e;
        const formatted = JSON.stringify(data, null, 2);
        setJsonModal({ open: true, data: data });
    }

    const handleArchive = (record) => {
        update(
            'v1/mypos_direct_transactions',
            record.id,
            { archived: true },
            record
        );
        notifySuccess('custom.element_archived');
        triggerRefreshSummary();
    }

    const triggerRefreshSummary = () => {
        setRefreshSummary(prev => prev + 1);
    }
    return (
        <>
            {getRight() >= config.permissions.SUPERADMIN.value && (
                <JobStatus resourceUrl="/v1/mypos_direct_transactions" />
            )}
            <Summary refreshSummary={refreshSummary} />
            <List {...props} exporter={false} filters={filters} perPage={50} pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100, 250]} />} bulkActionButtons={<PostBulkActionButtons refreshSummary={triggerRefreshSummary} />} filterDefaultValues={{archived: false, event_id: '__none__'}} sort={{field:'date', order: 'DESC'}}>
                <ResponsiveDatagrid 
                    mobile={{
                        title: r => new Date(r.date).toLocaleString(),
                        subtitle: r => `${r.terminal_id} - ${r.myposData?.terminal_name || r.terminal_code || ''}`,
                        lines: [r => r.archived ? translate('custom.archived') : null],
                        right: r => <AmountField record={r} source="amount" />,
                    }}
                    onClick={(record) => { showJsonDetails(record) }}
                    swipeAction={{
                            icon: <Archive color="success" />,
                            onClick: handleArchive,
                            condition: r => !r.archived
                    }}
                >
                    <TextField source="type" />
                    <DateField source="date" showTime/>
                    <TextField source="terminal_id" label={translate('custom.mypos_direct.terminal_id')}/>
                    <TextField source="myposData.terminal_name" label={translate('custom.mypos_direct.terminal_name')} />
                    <TextField source="terminal_code" label={translate('custom.mypos_direct.terminal_code')} />
                    <AmountField source="amount" />
                    <ReferenceField source="event_id" reference="events">
                        <TextField source="name" />
                    </ReferenceField>
                    <JsonModalField source="myposData" />
                    <ArchiveButton refreshSummary={triggerRefreshSummary} />
                </ResponsiveDatagrid>
            </List>
            <JsonModal
                open={jsonModal.open}
                onClose={() => setJsonModal({ open: false, data: null })}
                data={jsonModal.data}
            />
        </>
    );
}